/* eslint-disable react/no-unstable-nested-components --
 * MasterDataTable の columns 配列に並ぶ cell 関数 / renderRowActions は React
 * component ではなくテーブルセルの render 関数 (AdminUploads/index.tsx と同判断)。
 */
import { useState } from 'react'

import { timestampDate } from '@bufbuild/protobuf/wkt'
import type { CsvUploadHistory } from '@ebalance/proto/bffuser/v1/csv_upload_history_model_pb'
import {
  UploadDataType,
  UploadResult,
} from '@ebalance/proto/bffuser/v1/csv_upload_history_model_pb'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'

import {
  Badge,
  Box,
  HStack,
  NativeSelectField,
  NativeSelectIndicator,
  NativeSelectRoot,
  TertiaryButton,
  Text,
  TextButton,
} from '@enechain/ecloud-designsystem'
import {
  FaChevronLeftRegular,
  FaChevronRightRegular,
} from '@enechain/ecloud-icons'

import { CompactAlert } from '~/components/common/CompactAlert'
import {
  COMPACT_DATE_FIELD_WIDTH,
  CompactCivilDateField,
} from '~/components/common/CompactCivilDateField'
import { HiddenFieldLabel } from '~/components/common/HiddenFieldLabel'
import {
  MasterDataTable,
  type MasterDataTableColumn,
} from '~/components/common/MasterDataTable'
import { PageBody } from '~/components/common/PageBody'
import { PageIntro } from '~/components/common/PageIntro'
import { PageLayout } from '~/components/common/PageLayout'
import { PageScrollArea } from '~/components/common/PageScrollArea'
import { iconSize } from '~/constants/iconSize'
import { useListMembers } from '~/hooks/member/useListMembers'
import { type CivilDate, civilDateToProtoDate } from '~/models/civilDate'
import { formatDateTimeFull } from '~/models/dateTime'
import { resolveMemberDisplayName } from '~/models/member'
import { DetailModal } from '~/pages/AdminUploadHistory/components/DetailModal'
import { useListCsvUploadHistories } from '~/pages/AdminUploadHistory/hooks/useListCsvUploadHistories'
import {
  dataTypeLabelKey,
  dataTypeUploadRoute,
} from '~/pages/AdminUploadHistory/models/dataType'
import { routePaths } from '~/routes/routePaths'

const PAGE_SIZE = 50
const ALL_VALUE = ''

type FilterState = {
  dataTypeFilter: UploadDataType
  uploadedByFilter: string
  resultFilter: UploadResult
  uploadedFrom: CivilDate | undefined
  uploadedTo: CivilDate | undefined
  page: number
}

const PERIOD_FROM_INPUT_ID = 'adminUploadHistory-periodFrom'
const PERIOD_TO_INPUT_ID = 'adminUploadHistory-periodTo'

const INITIAL_FILTER: FilterState = {
  dataTypeFilter: UploadDataType.UNSPECIFIED,
  uploadedByFilter: ALL_VALUE,
  resultFilter: UploadResult.UNSPECIFIED,
  uploadedFrom: undefined,
  uploadedTo: undefined,
  page: 1,
}

/**
 * CSVアップロード履歴画面 (design `admin/uploads/history.html`,
 * docs/specs/admin/data/uploads/history.md)。
 *
 * 一覧 (投入日時降順) + フィルタ (データ種別 / 投入者 / 結果 / 期間) +
 * 詳細モーダル (件数サマリー/エラー要因) + 失敗行の再アップロード導線。
 * 一覧はデフォルトで全件表示で、期間フィルタで絞り込む
 * (docs/specs/admin/data/uploads/history.md 前提)。
 */
export const AdminUploadHistoryPage = (): React.ReactElement => {
  const { t } = useTranslation()
  const [filter, setFilter] = useState<FilterState>(INITIAL_FILTER)
  const [detailTarget, setDetailTarget] = useState<
    CsvUploadHistory | undefined
  >(undefined)

  const { data: membersData } = useListMembers()
  const members = membersData?.members ?? []

  const { data, isLoading, isError, refetch } = useListCsvUploadHistories({
    dataTypeFilter:
      filter.dataTypeFilter === UploadDataType.UNSPECIFIED
        ? undefined
        : filter.dataTypeFilter,
    uploadedByFilter:
      filter.uploadedByFilter === ALL_VALUE
        ? undefined
        : filter.uploadedByFilter,
    resultFilter:
      filter.resultFilter === UploadResult.UNSPECIFIED
        ? undefined
        : filter.resultFilter,
    uploadedFrom:
      filter.uploadedFrom !== undefined
        ? civilDateToProtoDate(filter.uploadedFrom)
        : undefined,
    uploadedTo:
      filter.uploadedTo !== undefined
        ? civilDateToProtoDate(filter.uploadedTo)
        : undefined,
    page: filter.page,
    pageSize: PAGE_SIZE,
  })

  const histories = data?.histories ?? []
  const totalCount = data?.totalCount ?? 0
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE))

  const handleFilterChange = (partial: Partial<FilterState>): void => {
    // フィルタ変更時はページを 1 に戻す (page 以外の変更のみ、page 自体の変更はそのまま)。
    setFilter((prev) => ({ ...prev, ...partial, page: partial.page ?? 1 }))
  }

  const handleReset = (): void => {
    setFilter(INITIAL_FILTER)
  }

  const uploaderNameFor = (memberId: string): string =>
    resolveMemberDisplayName(members, memberId) ?? memberId

  const columns: Array<MasterDataTableColumn<CsvUploadHistory>> = [
    {
      key: 'uploadedAt',
      header: t('page:adminUploadHistory.column.uploadedAt'),
      align: 'center',
      cell: (row) =>
        row.uploadedAt !== undefined
          ? formatDateTimeFull(timestampDate(row.uploadedAt))
          : '',
    },
    {
      key: 'uploadedBy',
      header: t('page:adminUploadHistory.column.uploadedBy'),
      cell: (row) => uploaderNameFor(row.uploadedBy),
    },
    {
      key: 'dataType',
      header: t('page:adminUploadHistory.column.dataType'),
      cell: (row) => t(dataTypeLabelKey(row.dataType)),
    },
    {
      key: 'result',
      header: t('page:adminUploadHistory.column.result'),
      cell: (row) =>
        row.result === UploadResult.FAILED ? (
          <Badge status="negative">
            {t('page:adminUploadHistory.result.failed')}
          </Badge>
        ) : (
          <Badge status="positive">
            {t('page:adminUploadHistory.result.success')}
          </Badge>
        ),
    },
    {
      key: 'counts',
      header: t('page:adminUploadHistory.column.counts'),
      align: 'right',
      cell: (row) =>
        row.result === UploadResult.FAILED ? (
          <Text color="Text.lowEmphasis">—</Text>
        ) : (
          `${row.unchangedCount.toLocaleString()} / ${row.newCount.toLocaleString()} / ${row.changedCount.toLocaleString()}`
        ),
    },
  ]

  return (
    <PageLayout
      breadcrumbItems={[
        {
          label: t('page:adminUploadHistory.breadcrumb.admin'),
          to: routePaths.adminHub.path,
        },
        {
          label: t('common:route.adminUploads'),
          to: routePaths.adminUploads.path,
        },
        { label: t('common:route.adminUploadHistory'), bold: true },
      ]}
    >
      <PageBody>
        {/* ページ説明 (design admin/uploads/history.html `.page-description`)。
            外側 margin は付けない (PageIntro は縦 spacing を持たない契約で、
            説明文-フィルタ間は PageBody の gap 16px が一意に決める。
            design `#occto-tab-desc` の margin-bottom も `--space-4` = 16px)。 */}
        <PageIntro description={t('page:adminUploadHistory.description')} />

        <Box
          marginBottom={3}
          padding={3}
          borderWidth="1px"
          borderColor="Border.lowEmphasis"
          borderRadius="lg"
          backgroundColor="Surface.primary"
        >
          <HStack alignItems="flex-end" flexWrap="wrap" gap={4}>
            <Box>
              <Text textStyle="filterLabel" marginBottom={1}>
                {t('page:adminUploadHistory.filter.dataType')}
              </Text>
              <NativeSelectRoot width="180px" size="sm">
                <NativeSelectField
                  aria-label={t('page:adminUploadHistory.filter.dataType')}
                  onChange={(e) =>
                    handleFilterChange({
                      dataTypeFilter: Number(e.target.value) as UploadDataType,
                    })
                  }
                  value={String(filter.dataTypeFilter)}
                >
                  <option value={String(UploadDataType.UNSPECIFIED)}>
                    {t('page:adminUploadHistory.filter.selectPlaceholder')}
                  </option>
                  <option value={String(UploadDataType.SUPPLY_48_DATA)}>
                    {t('page:adminUploadHistory.dataType.supply48Data')}
                  </option>
                  <option value={String(UploadDataType.BILATERAL_TRADE)}>
                    {t('page:adminUploadHistory.dataType.bilateralTrade')}
                  </option>
                  <option value={String(UploadDataType.CIS_CONTRACT)}>
                    {t('page:adminUploadHistory.dataType.cisContract')}
                  </option>
                  <option value={String(UploadDataType.SPOT_BID_NORMAL)}>
                    {t('page:adminUploadHistory.dataType.spotBidNormal')}
                  </option>
                  <option value={String(UploadDataType.SPOT_BID_BLOCK)}>
                    {t('page:adminUploadHistory.dataType.spotBidBlock')}
                  </option>
                  <option value={String(UploadDataType.INTRADAY_BID)}>
                    {t('page:adminUploadHistory.dataType.intradayBid')}
                  </option>
                  <option value={String(UploadDataType.CUSTOMER_CONTRACT)}>
                    {t('page:adminUploadHistory.dataType.customerContract')}
                  </option>
                </NativeSelectField>
                <NativeSelectIndicator />
              </NativeSelectRoot>
            </Box>

            <Box>
              <Text textStyle="filterLabel" marginBottom={1}>
                {t('page:adminUploadHistory.filter.uploadedBy')}
              </Text>
              <NativeSelectRoot width="160px" size="sm">
                <NativeSelectField
                  aria-label={t('page:adminUploadHistory.filter.uploadedBy')}
                  onChange={(e) =>
                    handleFilterChange({ uploadedByFilter: e.target.value })
                  }
                  value={filter.uploadedByFilter}
                >
                  <option value={ALL_VALUE}>
                    {t('page:adminUploadHistory.filter.selectPlaceholder')}
                  </option>
                  {members.map((m) => (
                    <option key={m.memberId} value={m.memberId}>
                      {m.displayName}
                    </option>
                  ))}
                </NativeSelectField>
                <NativeSelectIndicator />
              </NativeSelectRoot>
            </Box>

            <Box>
              <Text textStyle="filterLabel" marginBottom={1}>
                {t('page:adminUploadHistory.filter.result')}
              </Text>
              <NativeSelectRoot width="140px" size="sm">
                <NativeSelectField
                  aria-label={t('page:adminUploadHistory.filter.result')}
                  onChange={(e) =>
                    handleFilterChange({
                      resultFilter: Number(e.target.value) as UploadResult,
                    })
                  }
                  value={String(filter.resultFilter)}
                >
                  <option value={String(UploadResult.UNSPECIFIED)}>
                    {t('page:adminUploadHistory.filter.selectPlaceholder')}
                  </option>
                  <option value={String(UploadResult.SUCCESS)}>
                    {t('page:adminUploadHistory.result.success')}
                  </option>
                  <option value={String(UploadResult.FAILED)}>
                    {t('page:adminUploadHistory.result.failed')}
                  </option>
                </NativeSelectField>
                <NativeSelectIndicator />
              </NativeSelectRoot>
            </Box>

            <Box>
              <Text textStyle="filterLabel" marginBottom={1}>
                {t('page:adminUploadHistory.filter.period')}
              </Text>
              <HStack alignItems="center" gap={2}>
                <Box width={COMPACT_DATE_FIELD_WIDTH}>
                  <HiddenFieldLabel htmlFor={PERIOD_FROM_INPUT_ID}>
                    {t('page:adminUploadHistory.filter.periodFrom')}
                  </HiddenFieldLabel>
                  <CompactCivilDateField
                    hasClear
                    id={PERIOD_FROM_INPUT_ID}
                    onChange={(d) => handleFilterChange({ uploadedFrom: d })}
                    value={filter.uploadedFrom}
                  />
                </Box>
                <Text color="Text.midEmphasis">〜</Text>
                <Box width={COMPACT_DATE_FIELD_WIDTH}>
                  <HiddenFieldLabel htmlFor={PERIOD_TO_INPUT_ID}>
                    {t('page:adminUploadHistory.filter.periodTo')}
                  </HiddenFieldLabel>
                  <CompactCivilDateField
                    hasClear
                    id={PERIOD_TO_INPUT_ID}
                    onChange={(d) => handleFilterChange({ uploadedTo: d })}
                    value={filter.uploadedTo}
                  />
                </Box>
              </HStack>
            </Box>

            <TextButton onClick={handleReset} size="sm">
              {t('page:adminUploadHistory.filter.clear')}
            </TextButton>
          </HStack>
        </Box>

        {isError && (
          <Box marginBottom={3}>
            <CompactAlert
              actions={
                <TertiaryButton onClick={() => void refetch()} size="sm">
                  {t('page:adminUploadHistory.error.retry')}
                </TertiaryButton>
              }
              role="alert"
              status="error"
              title={t('page:adminUploadHistory.error.fetchFailed')}
            />
          </Box>
        )}

        <PageScrollArea>
          <MasterDataTable
            actionColWidth="196px"
            columns={columns}
            emptyLabel={
              filter.dataTypeFilter !== UploadDataType.UNSPECIFIED ||
              filter.uploadedByFilter !== ALL_VALUE ||
              filter.resultFilter !== UploadResult.UNSPECIFIED ||
              filter.uploadedFrom !== undefined ||
              filter.uploadedTo !== undefined
                ? t('page:adminUploadHistory.empty.filtered')
                : t('page:adminUploadHistory.empty.noHistory')
            }
            getRowKey={(row) => row.id}
            isLoading={isLoading}
            renderRowActions={(row) => (
              <HStack justifyContent="center" gap={2}>
                <TextButton onClick={() => setDetailTarget(row)} size="md">
                  {t('page:adminUploadHistory.action.detail')}
                </TextButton>
                {row.result === UploadResult.FAILED &&
                  dataTypeUploadRoute(row.dataType) !== undefined && (
                    <TertiaryButton asChild size="md">
                      <Link to={dataTypeUploadRoute(row.dataType) ?? '#'}>
                        {t('page:adminUploadHistory.action.reupload')}
                      </Link>
                    </TertiaryButton>
                  )}
              </HStack>
            )}
            rows={histories}
          />
        </PageScrollArea>

        {!isLoading && totalCount > 0 && (
          <HStack justifyContent="center" gap={3} marginTop={3}>
            <TertiaryButton
              disabled={filter.page <= 1}
              onClick={() => handleFilterChange({ page: filter.page - 1 })}
              size="sm"
            >
              {/* ページ送りの ← → は DS icon で描く。 従来は i18n 文言に矢印を
                  埋めていたためフォント依存で、 翻訳のたびに矢印を書き写す必要も
                  あった。 design css `.btn:not(...)>svg` = 20px。 */}
              <FaChevronLeftRegular boxSize={iconSize.labeledButton} />
              {t('page:adminUploadHistory.pagination.prev')}
            </TertiaryButton>
            <Text color="Text.midEmphasis" fontSize="sm">
              {t('page:adminUploadHistory.pagination.label', {
                page: filter.page,
                totalPages,
                totalCount,
              })}
            </Text>
            <TertiaryButton
              disabled={filter.page >= totalPages}
              onClick={() => handleFilterChange({ page: filter.page + 1 })}
              size="sm"
            >
              {t('page:adminUploadHistory.pagination.next')}
              <FaChevronRightRegular boxSize={iconSize.labeledButton} />
            </TertiaryButton>
          </HStack>
        )}
      </PageBody>

      <DetailModal
        history={detailTarget}
        isOpen={detailTarget !== undefined}
        onClose={() => setDetailTarget(undefined)}
        uploaderName={
          detailTarget !== undefined
            ? uploaderNameFor(detailTarget.uploadedBy)
            : ''
        }
      />
    </PageLayout>
  )
}
